
import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { OrderWithCustomer } from '../../types/orderTypes';

interface UseOrderSubmitProps {
  orderId: string;
  customerId: string;
  customerName: string;
  products: any[];
  orderNumber: string;
  orderDate: string;
  shippingAddress: string;
  notes: string;
  handleOrderUpdated: (updatedOrder: OrderWithCustomer) => void;
  handleBackToOrders: () => void;
  customerCountry?: string;
  customerRegion?: string;
  customerMunicipality?: string;
}

export const useOrderSubmit = ({
  orderId,
  customerId,
  customerName,
  products,
  orderNumber,
  orderDate,
  shippingAddress,
  notes,
  handleOrderUpdated,
  handleBackToOrders,
  customerCountry,
  customerRegion,
  customerMunicipality
}: UseOrderSubmitProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId || products.length === 0) {
      toast({
        title: "Validation échouée",
        description: "Veuillez sélectionner un client et ajouter au moins un produit.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Update customer location information if available
      if (customerId && (customerCountry || customerRegion || customerMunicipality)) {
        const { error: customerUpdateError } = await supabase
          .from('customers')
          .update({
            country: customerCountry || null,
            region: customerRegion || null,
            municipality: customerMunicipality || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', customerId);
          
        if (customerUpdateError) {
          console.error('Error updating customer location:', customerUpdateError);
        }
      }
      
      // Prepare shipping_metadata
      const shippingMetadata = {
        address: shippingAddress,
        country: customerCountry,
        region: customerRegion,
        municipality: customerMunicipality
      };
      
      // Update order with metadata
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .update({
          customer_id: customerId,
          order_number: orderNumber,
          order_date: orderDate,
          shipping_metadata: shippingMetadata,
          notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)
        .select('*')
        .single();
        
      if (orderError) throw orderError;
      
      // Delete existing order items
      const { error: deleteError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId);
        
      if (deleteError) throw deleteError;
      
      // Insert new order items
      const orderItems = products.map(product => ({
        order_id: orderId,
        product_id: product.product_id || null,
        product_name: product.name,
        quantity: product.quantity,
        unit_price: product.price,
        total_price: product.price * product.quantity,
        currency: product.currency || 'DZD'
      }));
      
      const { error: insertError } = await supabase
        .from('order_items')
        .insert(orderItems);
        
      if (insertError) throw insertError;
      
      // Get customer data
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();
        
      if (customerError) throw customerError;
      
      // Construct the updated order with customer data
      const updatedOrder = {
        ...orderData,
        customer: customerData,
        customer_name: customerData.name,
        items: products,
        total_amount: products.reduce((sum, product) => sum + (product.price * product.quantity), 0),
        shipping_metadata: shippingMetadata
      };
      
      toast({
        title: "Commande mise à jour",
        description: `La commande #${orderNumber} a été mise à jour avec succès.`,
      });
      
      handleOrderUpdated(updatedOrder);
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour de la commande.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit
  };
};
